/**
 * Deterministic Studio requested-package sync for the current project.
 * Authoritative graph/project package wins over defaults and prior-project state.
 * Graph package truth is bound to the selected project identity.
 */

export type ProjectPackageSyncInput = {
  readonly selectedProjectId: string | null;
  readonly lastSyncedProjectId: string | null;
  /** BusinessGraph.projectId for the retained active graph, when present. */
  readonly graphProjectId: string | null | undefined;
  readonly graphPackageId: string | undefined;
  readonly currentRequestedPackageId: string;
  /** True when the operator explicitly chose a package for the selected project. */
  readonly hasExplicitSelectionForCurrentProject: boolean;
};

export type ProjectPackageSyncResult = {
  readonly lastSyncedProjectId: string | null;
  readonly requestedPackageId: string;
  readonly changed: boolean;
};

export type ProjectPackageSnapshotReadyInput = {
  readonly selectedProjectId: string | null;
  readonly lastSyncedProjectId: string | null;
  readonly projectLoading: boolean;
  readonly graphProjectId: string | null | undefined;
};

const TEMP_DEFAULT_PACKAGE = "package-standard-5";

/**
 * Graph package is authoritative only when its graph project identity matches
 * the currently selected Studio project.
 */
export function resolveAuthoritativeGraphPackageId(input: {
  readonly selectedProjectId: string | null;
  readonly graphProjectId: string | null | undefined;
  readonly graphPackageId: string | undefined;
}): string | undefined {
  if (!input.selectedProjectId) return undefined;
  if (!input.graphProjectId || input.graphProjectId !== input.selectedProjectId) {
    return undefined;
  }
  return input.graphPackageId;
}

/**
 * Build/rebuild interactions require a current project snapshot identity.
 * Not ready when the selected project is unsynchronized, still loading, or the
 * retained graph belongs to another project. A matching graph (or no retained
 * graph) with loading false enables build after sync.
 */
export function isProjectPackageSnapshotReady(
  input: ProjectPackageSnapshotReadyInput,
): boolean {
  if (!input.selectedProjectId) return false;
  if (input.lastSyncedProjectId !== input.selectedProjectId) return false;
  if (input.projectLoading) return false;
  if (
    input.graphProjectId != null &&
    input.graphProjectId !== input.selectedProjectId
  ) {
    return false;
  }
  return true;
}

/**
 * Resolve requested package for the selected Studio project.
 * - On project switch: use matching-graph package when available, else temporary default.
 * - When graph arrives later: replace temporary/default or stale values unless the
 *   operator made an explicit selection for this project.
 * - A graph package from another project is never accepted.
 */
export function syncRequestedPackageForProject(
  input: ProjectPackageSyncInput,
): ProjectPackageSyncResult {
  if (!input.selectedProjectId) {
    return {
      lastSyncedProjectId: null,
      requestedPackageId: input.currentRequestedPackageId,
      changed: input.lastSyncedProjectId !== null,
    };
  }

  const graphPackageId = resolveAuthoritativeGraphPackageId({
    selectedProjectId: input.selectedProjectId,
    graphProjectId: input.graphProjectId,
    graphPackageId: input.graphPackageId,
  });

  const projectChanged = input.lastSyncedProjectId !== input.selectedProjectId;
  if (projectChanged) {
    const next = graphPackageId ?? TEMP_DEFAULT_PACKAGE;
    return {
      lastSyncedProjectId: input.selectedProjectId,
      requestedPackageId: next,
      changed: true,
    };
  }

  if (!graphPackageId) {
    return {
      lastSyncedProjectId: input.selectedProjectId,
      requestedPackageId: input.currentRequestedPackageId,
      changed: false,
    };
  }

  if (input.hasExplicitSelectionForCurrentProject) {
    return {
      lastSyncedProjectId: input.selectedProjectId,
      requestedPackageId: input.currentRequestedPackageId,
      changed: false,
    };
  }

  if (input.currentRequestedPackageId !== graphPackageId) {
    return {
      lastSyncedProjectId: input.selectedProjectId,
      requestedPackageId: graphPackageId,
      changed: true,
    };
  }

  return {
    lastSyncedProjectId: input.selectedProjectId,
    requestedPackageId: input.currentRequestedPackageId,
    changed: false,
  };
}

/**
 * agencyRequestedSitePackageId should only be submitted when it differs from the
 * built package and is not merely the ambient UI default for an always-Basic-3 project.
 */
export function resolveAgencyRequestedForBuild(input: {
  readonly builtSitePackageId: string;
  readonly requestedSitePackageId: string;
  readonly eligibilityRequestedPackage?: string;
}): string | undefined {
  const agencyRequested =
    input.eligibilityRequestedPackage ?? input.requestedSitePackageId;
  if (agencyRequested === input.builtSitePackageId) {
    return undefined;
  }
  return agencyRequested;
}
